import { useAuth } from "@/lib/auth";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { CLOUDFLARE_URL } from "@/lib/config";
import { Calendar, Info, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

interface BillingInfo {
    amount: number | null;
    currency: string | null;
    interval: string | null;
    currentPeriodEnd: number | null;
    cancelAtPeriodEnd: boolean;
}

interface CardInfo {
    last4: string;
    brand: string;
    expMonth: number;
    expYear: number;
}

interface BillingData {
    subscription: {
        planType: string;
        planStart: string | null;
        planEnd: string | null;
    } | null;
    billingInfo: BillingInfo | null;
    cardInfo: CardInfo | null;
}

const PLAN_DISPLAY_NAMES: Record<string, string> = {
    pro: "Learn+",
    style: "Style+",
    prolite: "Style+",
    free: "Free",
    trial: "Trial",
    trialThenPro: "Trial",
};

export const BillingTab = () => {
    const { authenticatedClient, authenticatedFetch, loadAuthModal } = useCommunication();
    const { planType } = useAuth();

    const [billingData, setBillingData] = useState<BillingData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchBillingInfo();
    }, []);

    async function fetchBillingInfo() {
        try {
            const response = await authenticatedFetch(`${CLOUDFLARE_URL}/users/billing-info`, {
                method: "GET",
            });

            if (response.ok) {
                const data = await response.json();
                setBillingData(data.message);
            }
        } catch (error) {
            console.error("Failed to fetch billing info:", error);
        } finally {
            setLoading(false);
        }
    }

    async function openStripePortal() {
        const popup = window.open("", "stripe-portal", "width=1000,height=720");

        const client = await authenticatedClient();
        const res = await client.functions.invoke("create-portal", {
            method: "POST",
            body: {
                returnUrl: window.location.origin + "/portal-return"
            },
        });

        const { url } = await res.data?.message;

        if (url && popup) {
            popup.location.href = url;
            return;
        }
    }

    const openUpgradeModal = () => {
        loadAuthModal("upgrade");
    };

    const formatNextBillDate = (timestamp: number | null | undefined) => {
        if (!timestamp) return null;
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const formatAmount = (amount: number | null | undefined, currency: string | null | undefined) => {
        if (amount === null || amount === undefined) return null;
        const formatter = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency?.toUpperCase() || "USD",
        });
        return formatter.format(amount);
    };

    const displayPlanType = billingData?.subscription?.planType || planType || "free";
    const planDisplayName = PLAN_DISPLAY_NAMES[displayPlanType] || "Free";
    const isPaidPlan = ["pro", "style", "prolite", "trial", "trialThenPro"].includes(displayPlanType);
    const isTrial = ["trial", "trialThenPro"].includes(displayPlanType);
    const isCancelled = billingData?.billingInfo?.cancelAtPeriodEnd;

    const nextBillDate = formatNextBillDate(billingData?.billingInfo?.currentPeriodEnd);
    const billAmount = formatAmount(billingData?.billingInfo?.amount, billingData?.billingInfo?.currency);
    const interval = billingData?.billingInfo?.interval;

    if (loading) {
        return (
            <div className="flex flex-col gap-4 p-4 h-full items-center justify-center">
                <Loader2 className="size-6 animate-spin text-theme-1" />
            </div>
        );
    }

    const isMonthlyPlan = !interval || interval === "month";
    const showBillingBanner = isPaidPlan && !isTrial;
    const showYearlyUpsell = isPaidPlan && isMonthlyPlan && !isCancelled && !isTrial;

    return (
        <div className="flex flex-col gap-4 p-4 h-full">
            <h1 className="font-bold text-2xl text-theme-0 mb-2">{planDisplayName}</h1>

            {showBillingBanner && (
                <div className="bg-theme-1 rounded-xl px-4 py-3.5 flex items-center gap-3">
                    <Calendar className="size-[18px] text-theme-0 flex-shrink-0" strokeWidth={1.5} />
                    <p className="text-sm text-theme-0">
                        {isCancelled ? (
                            <>Your plan ends on {nextBillDate || "soon"}.</>
                        ) : nextBillDate && billAmount ? (
                            <>
                                Your next bill is {billAmount} on {nextBillDate}.{"   "}
                                <button
                                    onClick={openStripePortal}
                                    className="underline hover:no-underline"
                                >
                                    See billing details
                                </button>
                            </>
                        ) : (
                            <>
                                View your billing information.{"   "}
                                <button
                                    onClick={openStripePortal}
                                    className="underline hover:no-underline"
                                >
                                    See billing details
                                </button>
                            </>
                        )}
                    </p>
                </div>
            )}

            {isTrial && (
                <div className="bg-theme-1 rounded-xl px-4 py-3.5 flex items-center gap-3">
                    <Calendar className="size-[18px] text-theme-0 flex-shrink-0" strokeWidth={1.5} />
                    <p className="text-sm text-theme-0">
                        Your trial ends on {nextBillDate || "soon"}.
                    </p>
                </div>
            )}

            {showYearlyUpsell && (
                <div className="bg-theme-1 rounded-xl px-4 py-3.5 flex items-center gap-3 opacity-60">
                    <Info className="size-[18px] text-theme-0 flex-shrink-0" strokeWidth={1.5} />
                    <p className="text-sm text-theme-0">
                        Save with a yearly {planDisplayName} plan — <span className="text-theme-1">Coming soon</span>
                    </p>
                </div>
            )}

            {billAmount && interval && (
                <p className="text-lg font-semibold text-theme-0 mt-2">
                    {billAmount}/{interval}
                </p>
            )}

            <div className="flex flex-wrap gap-2">
                <button
                    onClick={openUpgradeModal}
                    style={{ background: 'var(--bctext-0)', color: 'var(--bcbackground-0)' }}
                    className="px-5 py-2 rounded-full font-medium text-sm hover:opacity-90 transition-opacity"
                >
                    Upgrade
                </button>
                {isPaidPlan && !isCancelled && (
                    <button
                        onClick={openStripePortal}
                        className="bg-theme-1 text-theme-0 px-5 py-2 rounded-full font-medium text-sm hover:brightness-95 transition-all"
                    >
                        Cancel plan
                    </button>
                )}
                {isPaidPlan && (
                    <button
                        onClick={openStripePortal}
                        className="bg-theme-1 text-theme-0 px-5 py-2 rounded-full font-medium text-sm hover:brightness-95 transition-all"
                    >
                        Manage billing
                    </button>
                )}
            </div>

            <p className="text-sm text-[#666] dark:text-theme-1 mt-1">
                View all plans & features on the{" "}
                <a
                    href="/upgrade"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:no-underline"
                >
                    Pricing
                </a>{" "}
                page.
            </p>
        </div>
    );
};